import { Inject } from '@odinjs/odin';

import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FieldAPI, FormAPI, InputAPI, PanelAPI } from '@philips-internal/oswald-ui-components/lib/components';

import { Endpoint } from '../../../../../constants/endpoints';
import { FragmentNames, NutritionWaterBalanceAliases } from '../../../../../codegen';
import BaseController from '../../../../../controllers/BaseController';
import InformationBarUtils from '../../../../../controllers/commons/header/information-bar-header-utils';
import { WaterBalanceTypes } from './water-balance-types';
import moment from 'moment';
import { ISO_DATE_INPUT } from '../../../../../constants/date-utils';

@Controller({ fragment: FragmentNames.NUTRITION_WATER_BALANCE })
class WaterBalanceViewController extends BaseController {

  @Inject(NutritionWaterBalanceAliases.PANEL_LOADER)
  panelLoaderAPI!: PanelAPI;

  @Inject(NutritionWaterBalanceAliases.PANEL_CONTENT)
  panelContentAPI!: PanelAPI;

  @Inject(NutritionWaterBalanceAliases.FORM_MAIN_INFORMATIONS)
  formMainInformationsAPI!: FormAPI;

  @Inject(NutritionWaterBalanceAliases.FORM_PERIOD_OF_VALIDITY)
  formPeriodValidityAPI!: FormAPI;

  @Inject(NutritionWaterBalanceAliases.FORM_ADDITIONAL_INFORMATION)
  formAddicionalInformationsAPI!: FormAPI;

  @Inject(NutritionWaterBalanceAliases.FIELD_JUSTIFICATION)
  fieldJustificationAPI!: FieldAPI;

  @Inject(NutritionWaterBalanceAliases.FIELD_PRESCRIBER_DATE)
  fieldPrescriberDtAPI!: FieldAPI;

  @Inject(NutritionWaterBalanceAliases.FIELD_PRESCRIBER_NAME)
  fieldPrescriberNameAPI!: FieldAPI;

  @Inject(NutritionWaterBalanceAliases.INPUT_START_DATE)
  inputStartDateAPI!: InputAPI;

  @Inject(NutritionWaterBalanceAliases.FIELD_CALORIC_WEIGHT_KCAL)
  fieldCaloricWeightKcalAPI!: FieldAPI;

  @Inject(NutritionWaterBalanceAliases.FIELD_MAXIMUM_DAILY_LIQUID_VOLUME_ML)
  fieldMaximumDailyLiquidVolumeMl!: FieldAPI;

  @OnMount(NutritionWaterBalanceAliases.PANEL_CONTENT)
  handleMountPanelCard(): void {
    InformationBarUtils.collapsedBar = true;
    const { nutritionId, patientId } = this.historyService.getCurrentRouteParams()
    this.fetchWaterBalanceDetails(String(nutritionId), String(patientId))
  }

  @OnMount(NutritionWaterBalanceAliases.FORM_MAIN_INFORMATIONS)
  handleMountFormInformations(): void {
    const waterBalanceView = this.context.get('waterBalanceView') as WaterBalanceTypes
    if (waterBalanceView) {
      const {
        weight,
        bodySurface,
        caloricWeight,
        totalVolumeItems,
        totalDailyVolume,
        totalVolumeDiet,
        weightValue,
        height,
        maximumDailyUnit,
        maximumDailyVolume,
        maximumDietUnit,
        maximumItemsVolume,
        maximumNetVolume,
        maximumVolumeDiet,
        measurementUnitItems,
      } = waterBalanceView;

      this.formMainInformationsAPI.setValue({
        bodySurface,
        caloricWeight,
        height,
        maximumDailyUnit,
        maximumDailyVolume,
        maximumDietUnit,
        maximumItemsVolume,
        maximumNetVolume,
        maximumVolumeDiet,
        measurementUnitItems,
        totalDailyVolume,
        totalVolumeDiet,
        totalVolumeItems,
        weight,
        weightValue,
      })

      this.fieldCaloricWeightKcalAPI.setVisible(!!caloricWeight)
      this.fieldMaximumDailyLiquidVolumeMl.setVisible(!!maximumNetVolume)
    }
  }

  @OnMount(NutritionWaterBalanceAliases.FORM_PERIOD_OF_VALIDITY)
  handleMountFormPeriodOfValidity(): void {
    const waterBalanceView = this.context.get('waterBalanceView') as WaterBalanceTypes

    if (waterBalanceView) {
      const { duration } = waterBalanceView
      const startDate = moment(String(waterBalanceView.startDate)).utc().format(ISO_DATE_INPUT)

      this.formPeriodValidityAPI.setValue({
        duration,
        startDate,
      })

      this.inputStartDateAPI.setValue(startDate)
    }
  }

  @OnMount(NutritionWaterBalanceAliases.FORM_ADDITIONAL_INFORMATION)
  handleMountFormAddicionalInformations(): void {
    const waterBalanceView = this.context.get('waterBalanceView') as WaterBalanceTypes
    if (waterBalanceView) {
      const { prescriberDt, prescriberName, justification } = waterBalanceView

      this.formAddicionalInformationsAPI.setValue({
        justification,
        prescriberDt,
        prescriberName,
      })

      this.fieldJustificationAPI.setVisible(!!justification)
      this.fieldPrescriberNameAPI.setVisible(!!prescriberName)
      this.fieldPrescriberDtAPI.setVisible(!!prescriberDt)
    }
  }

  private fetchWaterBalanceDetails(nutritionId: string, patientId: string): void {
    this.panelLoaderAPI.setVisible(true)
    this.panelContentAPI.setVisible(false)
    HttpService.get(Endpoint.GET_CPOE_NUTRITION_WATER_BALANCE_DETAILS, { nutritionId, patientId })
      .then((response: HttpResponse) => {
        const waterBalanceView = response.body as WaterBalanceTypes;
        this.context.set({ waterBalanceView })
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR)
      }).finally(() => {
        this.panelLoaderAPI.setVisible(false)
        this.panelContentAPI.setVisible(true)
      })
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'nutrition-water-balance',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}

export default WaterBalanceViewController;